'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useRef } from 'react';
import dynamic from 'next/dynamic';
import { type UserContext } from '@/types/user-context';
import { HeaderVisibilityRouteSync } from '@/app/components/header-visibility-route-sync/header-visibility-route-sync';
import { usePostLoginActionHandler } from '@/hooks/use-post-login-action-handler';
import { useInitPage } from '@/hooks/use-init-page';
import type { SiteWideJackpotData } from '@/types/aggregation/site-wide-jackpot';
import { GlobalProvider } from '@/store/contexts/global-context';
import type { ServerContext } from '@/store/server-context';
import { PageViewTracker } from '../components/page-tracker';
import { LoginGeolocationEffects } from '@/app/components/geolocation/login-geolocation-effects';
import { GameWindowExperimentEffects } from '@/app/components/game-window-experiment-effects/game-window-experiment-effects';

// Mount UMModals Async and don't wrap anything so it won't break SSR but still render the modal on click
const UMModals = dynamic(
    () => import('@fanduel/cpe-um-bundle/core-modal-presenter/library').then((mod) => mod.UMModals),
    { ssr: false }
);

const EmbeddedGameShell = dynamic(
    () => import('@/app/components/embedded-game-shell/embedded-game-shell').then((mod) => mod.EmbeddedGameShell),
    { ssr: false }
);

type InitPageProps = {
    userContextResponse: UserContext | null;
    siteWideJackpot: SiteWideJackpotData;
    serverContext: ServerContext;
};

const InitPage = ({ userContextResponse, siteWideJackpot, serverContext }: InitPageProps) => {
    useInitPage({ userContextResponse, siteWideJackpot, serverContext });
    return null;
};

type LayoutClientProps = {
    children: React.ReactNode;
    userContextResponse: UserContext | null;
    siteWideJackpot: SiteWideJackpotData;
    serverContext: ServerContext;
};

const LayoutClient = ({ children, userContextResponse, siteWideJackpot, serverContext }: LayoutClientProps) => {
    const queryClient = useRef(new QueryClient());
    usePostLoginActionHandler();

    return (
        <GlobalProvider value={serverContext}>
            <LoginGeolocationEffects />
            <GameWindowExperimentEffects />
            <HeaderVisibilityRouteSync />
            <InitPage
                userContextResponse={userContextResponse}
                siteWideJackpot={siteWideJackpot}
                serverContext={serverContext}
            />
            <UMModals />
            <QueryClientProvider client={queryClient.current}>
                <EmbeddedGameShell />
                {children}
            </QueryClientProvider>
            <PageViewTracker />
        </GlobalProvider>
    );
};

export default LayoutClient;
